import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";

/**
 * USP <621> Corrective Actions
 *
 * Renders one collapsible orange-tinted block per failing suitability
 * criterion (N / As / Rs). If every criterion passes, renders a single green
 * "System Suitability Confirmed" badge instead.
 *
 * Uses the ``pass`` flag on each criterion — items with ``pass === true`` or
 * ``pass === null`` are ignored (null = insufficient data, not a failure).
 */
export default function UspCorrectiveActions({ suitability }) {
  const [openIdx, setOpenIdx] = useState(0);

  if (!suitability) return null;

  const failing = (suitability.criteria || []).filter((c) => c.pass === false);

  // ── Success path ── every computed criterion passed
  if (suitability.all_pass && failing.length === 0) {
    return (
      <div
        data-testid="usp-suitability-confirmed"
        style={{
          marginTop: 12,
          padding: "10px 14px",
          borderRadius: 6,
          border: "1px solid rgba(34,197,94,0.45)",
          background: "rgba(34,197,94,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 12,
          fontWeight: 600,
          color: "var(--text)",
        }}
      >
        <CheckCircle2 size={16} color="#16a34a" />
        System Suitability Confirmed — USP &lt;621&gt; compliant
      </div>
    );
  }

  if (failing.length === 0) return null;

  return (
    <div
      data-testid="usp-corrective-actions"
      style={{
        marginTop: 12,
        border: "1px solid rgba(234,88,12,0.35)",
        background: "rgba(234,88,12,0.06)",
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          fontSize: 11,
          fontFamily: "var(--mono)",
          textTransform: "uppercase",
          letterSpacing: 1,
          color: "#ea580c",
          fontWeight: 700,
          background: "rgba(234,88,12,0.10)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <AlertTriangle size={14} />
        Corrective Actions ({failing.length} failing criteri{failing.length === 1 ? "on" : "a"})
      </div>
      {failing.map((crit, idx) => {
        const advice = buildAdvice(crit);
        const isOpen = openIdx === idx;
        return (
          <div
            key={`ca-${idx}`}
            data-testid={`corrective-action-${slug(crit.name)}`}
            style={{ borderTop: idx === 0 ? "none" : "1px solid rgba(234,88,12,0.15)" }}
          >
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : idx)}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
                  {crit.name}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--mono)",
                    padding: "2px 6px",
                    borderRadius: 3,
                    background: "rgba(234,88,12,0.15)",
                    color: "#ea580c",
                    fontWeight: 700,
                  }}
                >
                  FAIL
                </span>
                <span style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                  {crit.observed} · req. {crit.threshold}
                </span>
              </div>
              {isOpen ? <ChevronUp size={14} color="#ea580c" /> : <ChevronDown size={14} color="#ea580c" />}
            </button>
            {isOpen && (
              <div
                style={{
                  padding: "0 14px 12px",
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: "var(--text2)",
                }}
              >
                <div style={{ marginBottom: 6, fontWeight: 600, color: "var(--text)" }}>
                  ⚠ {advice.headline}
                </div>
                {advice.causes && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--text3)", marginBottom: 4 }}>
                      Possible causes
                    </div>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      {advice.causes.map((c, i) => <li key={i}>{c}</li>)}
                    </ol>
                  </div>
                )}
                {advice.recommendation && (
                  <div>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--text3)", marginBottom: 4 }}>
                      Recommended action
                    </div>
                    <div>{advice.recommendation}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── advice lookup ────────────────────────────────────────────────────────

function buildAdvice(crit) {
  const name = (crit.name || "").toLowerCase();
  const value = extractValue(crit.observed);

  if (name.includes("theoretical") || name.startsWith("n") || /\bplates?\b/.test(name)) {
    return {
      headline: `N = ${value ?? "—"} is below USP <621> requirement (≥ 2,000).`,
      causes: [
        "Column degradation — check column age and pressure drop.",
        "Excessive flow rate — try reducing by 20–30%.",
        "Large extra-column volume — check tubing connections and injector.",
        "Poor sample solubility — verify mobile phase compatibility.",
      ],
      recommendation:
        "Run a system suitability standard (e.g. caffeine or uracil) to confirm column performance.",
    };
  }

  if (name.includes("tailing") || name.includes("symmetry") || /\bas\b/.test(name)) {
    const numeric = Number.parseFloat(value);
    const isTailing = Number.isFinite(numeric) && numeric > 2.0;
    const isFronting = Number.isFinite(numeric) && numeric < 0.8;
    let branch = "";
    if (isTailing) {
      branch =
        "As > 2.0 (tailing): check for column overload, secondary interactions with the stationary phase, or dead volume in fittings.";
    } else if (isFronting) {
      branch =
        "As < 0.8 (fronting): check for column overload or incorrect pH of the mobile phase.";
    } else {
      branch =
        "If As > 2.0 (tailing): check for column overload, secondary interactions, or dead volume. If As < 0.8 (fronting): check for column overload or incorrect pH of mobile phase.";
    }
    return {
      headline: `As = ${value ?? "—"} is outside USP <621> range (0.8–2.0).`,
      recommendation: branch,
    };
  }

  if (name.includes("resolution") || /\brs\b/.test(name)) {
    return {
      headline: `Rs = ${value ?? "—"} is below USP <621> requirement (≥ 2.0).`,
      causes: [
        "Peaks too close in retention time — adjust mobile phase composition.",
        "Peaks too broad — see N recommendations above.",
        "Column selectivity insufficient — consider a different stationary phase.",
      ],
      recommendation:
        "Modify gradient or isocratic conditions to increase retention time difference between critical peak pairs.",
    };
  }

  // Unknown criterion — fall back to a generic hint using the observed value.
  return {
    headline: `${crit.name} outside acceptance range (observed ${crit.observed}, required ${crit.threshold}).`,
    recommendation: "Review the corresponding method parameter and re-run a system suitability standard.",
  };
}

// The backend puts human-readable values in `observed` (e.g. "min N = 177",
// "worst As = 2.35", "min Rs = 1.23"). Pull the numeric part for display.
function extractValue(observed) {
  if (observed == null) return null;
  const s = String(observed);
  const m = s.match(/-?\d+(?:[\.,]\d+)?/);
  return m ? m[0].replace(",", ".") : s;
}

function slug(name) {
  return String(name || "criterion").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export { UspCorrectiveActions };
